import type { TelegramUser } from '~/types/User';
import { cloneStoreState } from '../utils/storeState.ts';

interface AccessRightsResponse {
  allParameters: Record<string, { available_values: string[]; default_value: string; type: string }>;
  userParameters: { parameter: string; value: string }[];
}

export interface AuthStore {
  isLogged: boolean;
  accessRights: Record<string, boolean> | null;
}

export const useAuthStore = createAuthStore({
  isLogged: false,
  accessRights: null,
});

export function createAuthStore(initialState: AuthStore) {
  return defineStore('auth', {
    state: () => cloneStoreState(initialState),

    actions: {
      async submitPhone(phone: string, captchaToken: string, agent: any) {
        try {
          const { data } = await useCustomFetch<{ message: string; status: string }>('/auth/login', {
            method: 'POST',
            body: { phone, captcha_token: captchaToken, agent },
          });

          if (data.value) {
            return data.value;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async validateCode(code: string) {
        try {
          const { data, error } = await useCustomFetch<{ message: string; status: string }>('/auth/validate-login', {
            method: 'POST',
            body: { code },
          });

          if (data.value) {
            this.isLogged = true;
            return data.value;
          }
          if (error.value?.data) {
            this.isLogged = false;
            return error.value.data;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async logout() {
        try {
          await useCustomFetch('/auth/logout', { method: 'POST' });

          this.isLogged = false;
          this.accessRights = null;
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async tgLogin(user: TelegramUser) {
        try {
          await useCustomFetch('/telegram/auth', {
            method: 'POST',
            body: user,
          });
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async tgLink(user: TelegramUser) {
        try {
          await useCustomFetch('/telegram/link', {
            method: 'POST',
            body: user,
          });
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async vkLogin() {
        try {
          let options = {};

          const returnUrl = localStorage.getItem('return-url');

          if (returnUrl) {
            const base64ReturnUrl = btoa(returnUrl);
            options = { params: { redirect_uri: base64ReturnUrl } };
            localStorage.removeItem('return-url');
          }

          const { data } = await useCustomFetch<{ auth_url: string }>('/vk', options);

          if (data.value?.auth_url) {
            await navigateTo(data.value?.auth_url, { external: true });
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async yandexLogin() {
        try {
          let options = {};

          const returnUrl = localStorage.getItem('return-url');

          if (returnUrl) {
            const base64ReturnUrl = btoa(returnUrl);
            options = { params: { redirect_uri: base64ReturnUrl } };
            localStorage.removeItem('return-url');
          }

          const { data } = await useCustomFetch<{ auth_url: string }>('/yandex', options);

          if (data.value?.auth_url) {
            await navigateTo(data.value?.auth_url, { external: true });
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },

      async unlinkSocial(socialLinkSlug: string) {
        try {
          const { data } = await useCustomFetch<{
            data: any;
            success?: boolean;
            error?: boolean;
          }>(`/${socialLinkSlug}/unlink`, { method: 'POST' });

          if (data.value) {
            return data.value;
          } else {
            return null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }

          return null;
        }
      },

      async getAccessRights() {
        try {
          const response = import.meta.client
            ? await useCustomFetchClient<{ data: AccessRightsResponse }>('/auth/access-rights')
            : (await useCustomFetch<{ data: AccessRightsResponse }>('/auth/access-rights')).data.value;

          if (response?.data?.userParameters?.length) {
            const parameters = Object.entries(response.data.allParameters) as [
              string,
              AccessRightsResponse['allParameters'][string],
            ][];

            parameters.forEach(([key, value]) => {
              this.accessRights = {
                ...this.accessRights,
                [key]: Boolean(Number(value.default_value)),
              };
            });

            for (const { parameter, value } of response.data.userParameters) {
              if (this.accessRights && value === '1') this.accessRights[parameter] = true;
            }
          } else {
            this.accessRights = null;
          }
        } catch (e) {
          if (e && process.env.NODE_ENV !== 'production') {
            console.error(e);
          }
        }
      },
    },
  });
}
